/**
 * Reportes → Series globales del mes (`/reportes/series-globales-mes`).
 *
 * Puerto de `Reportes::reporte_series_globales_mes_unificado` +
 * `Reportes::get_data_reporte_series_globales_mes` +
 * `Reportes_model::get_comprobante_by_serie_producto_fecha_totales` y
 * `get_market_data_by_series_state_date` +
 * `views/menu/reportes/reporte_series_globales_mes_unificado.php`.
 *
 * La pantalla del panel anterior tenía una pestaña por **tipo de producto**
 * (Combustibles, Extras, Market) y, dentro, otra por **estado** del comprobante
 * (Aceptado, Registrado, Rechazado, Anulado, Por anular, Pendiente). En cada
 * pestaña dibujaba una tablita por **serie** con sus productos (cantidad, precio
 * estimado y monto) y el total de la serie, y pedía los datos al servidor una vez
 * por cada combinación (con una consulta por serie y producto dentro).
 *
 * Reglas conservadas:
 * - Se factura **por serie y producto** del mes, con las cuatro fuentes
 *   (facturas, boletas, ventas de administración y ventas al crédito) y los mismos
 *   estados; los productos son los **activos** de tipo combustible o lubricante.
 * - Market sale de los documentos de `market_document_response`, agrupado por la
 *   descripción de cada ítem del JSON de productos.
 * - El precio estimado es el importe entre la cantidad y el estado sin dato cuenta
 *   como **Pendiente**.
 *
 * Diferencias anotadas:
 * - Los datos del mes se piden **una sola vez** y el panel los cruza: las pestañas
 *   se convierten en filtros de tipo y estado, y el resumen por estado (que el
 *   original obligaba a recorrer pestaña por pestaña) está siempre a la vista.
 * - Se muestran también los estados que el original no tenía en la lista
 *   («En Validación»), que antes quedaban fuera de todas las pestañas, y las series
 *   que no empiezan con `B` o `F` (el original sólo listaba esas).
 * - El detalle se muestra en una sola tabla agrupada por serie en lugar de una
 *   tablita por serie.
 */

import { ArrowLeft, Download, FileText, Info, RefreshCw, Search } from 'lucide-react';
import { Fragment, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';

import { Alert } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { CampoFecha } from '@/components/ui/calendario';
import { Select } from '@/components/ui/select';
import { Skeleton } from '@/components/ui/skeleton';
import { Paginador } from '@/components/ui/paginador';
import { useDocumentTitle } from '@/hooks/use-document-title';
import { usePaginacion } from '@/hooks/use-paginacion';
import { abrirPdfAutenticado } from '@/lib/descargas';
import { ApiError } from '@/lib/http/api-error';
import { cn, formatCurrency, formatNumber } from '@/lib/utils';

import {
  reportesApi,
  useCatalogosSeriesGlobales,
  useSeriesGlobales,
  type FilaSerie,
  type FiltrosSeriesGlobales,
} from '../reportes.api';

const MESES = [
  'enero',
  'febrero',
  'marzo',
  'abril',
  'mayo',
  'junio',
  'julio',
  'agosto',
  'septiembre',
  'octubre',
  'noviembre',
  'diciembre',
];

const CONTROL =
  'h-11 w-full rounded-control border border-ink-200 bg-white px-3 text-sm text-ink-900 ' +
  'transition-colors focus:border-brand-400 focus:ring-4 focus:ring-brand-100 focus:outline-none';

/** Cantidad con tres decimales, como el panel anterior. */
const CANTIDAD = new Intl.NumberFormat('es-PE', {
  minimumFractionDigits: 3,
  maximumFractionDigits: 3,
});

/** Mes en curso como `AAAA-MM`. */
function mesActual(): string {
  const hoy = new Date();

  return `${hoy.getFullYear()}-${String(hoy.getMonth() + 1).padStart(2, '0')}`;
}

/** «abril 2026» a partir de `2026-04`. */
function etiquetaMes(periodo: string | null | undefined): string {
  if (!periodo || !/^\d{4}-\d{2}$/.test(periodo)) return periodo ?? '';

  const [anio, mes] = periodo.split('-');
  const nombre = MESES[Number(mes) - 1];

  return nombre ? `${nombre} ${anio}` : periodo;
}

/** Agrupación de las filas por serie. */
interface GrupoDeSerie {
  serie: string;
  filas: FilaSerie[];
  cantidad: number;
  total: number;
}

export function ReporteSeriesGlobalesPage() {
  useDocumentTitle('Reportes · Series globales del mes');

  const [filtros, setFiltros] = useState<FiltrosSeriesGlobales>({
    periodo: mesActual(),
    tipo: 'COMBUSTIBLE',
    estado: '',
  });
  const [aviso, setAviso] = useState<string | null>(null);

  const catalogos = useCatalogosSeriesGlobales();
  const consulta = useSeriesGlobales(filtros);

  const meses = catalogos.data?.meses ?? [];
  const ultimoMesConDatos = meses[0]?.periodo ?? null;

  /** Filas del tipo y estado elegidos, sin productos en cero. */
  const filas = useMemo(
    () =>
      (consulta.data?.filas ?? []).filter(
        (fila) =>
          (filtros.tipo === 'GENERAL' || fila.tipo === filtros.tipo) &&
          (filtros.estado === '' || fila.estado === filtros.estado) &&
          (Number(fila.cantidad ?? 0) > 0 || Number(fila.total ?? 0) > 0),
      ),
    [consulta.data, filtros.tipo, filtros.estado],
  );

  const series = useMemo(() => {
    const acumulado = new Map<string, GrupoDeSerie>();

    for (const fila of filas) {
      const grupo = acumulado.get(fila.serie) ?? {
        serie: fila.serie,
        filas: [],
        cantidad: 0,
        total: 0,
      };

      grupo.filas.push(fila);
      grupo.cantidad += Number(fila.cantidad ?? 0);
      grupo.total += Number(fila.total ?? 0);
      acumulado.set(fila.serie, grupo);
    }

    return [...acumulado.values()].sort((a, b) => a.serie.localeCompare(b.serie));
  }, [filas]);

  const paginacion = usePaginacion(series, { clave: filtros });

  /** Resumen por estado del tipo elegido (o de todos los tipos). */
  const porEstado = useMemo(() => {
    const resumen = new Map<
      string,
      { estado: string; series: Set<string>; filas: number; cantidad: number; total: number }
    >();

    for (const fila of consulta.data?.filas ?? []) {
      if (filtros.tipo !== 'GENERAL' && fila.tipo !== filtros.tipo) continue;

      const actual = resumen.get(fila.estado) ?? {
        estado: fila.estado,
        series: new Set<string>(),
        filas: 0,
        cantidad: 0,
        total: 0,
      };

      actual.series.add(fila.serie);
      actual.filas += 1;
      actual.cantidad += Number(fila.cantidad ?? 0);
      actual.total += Number(fila.total ?? 0);
      resumen.set(fila.estado, actual);
    }

    // El orden del panel anterior primero y luego lo que aparezca en los datos.
    const orden = [...(consulta.data?.estados ?? []), ...resumen.keys()];
    const vistos: string[] = [];

    for (const estado of orden) {
      if (!vistos.includes(estado)) vistos.push(estado);
    }

    return vistos
      .filter((estado) => resumen.has(estado))
      .map((estado) => ({ ...resumen.get(estado)!, estado }));
  }, [consulta.data, filtros.tipo]);

  const totales = useMemo(
    () =>
      filas.reduce(
        (suma, fila) => ({
          cantidad: suma.cantidad + Number(fila.cantidad ?? 0),
          total: suma.total + Number(fila.total ?? 0),
        }),
        { cantidad: 0, total: 0 },
      ),
    [filas],
  );

  const sinDatos = Boolean(
    consulta.data && filas.length === 0 && ultimoMesConDatos && ultimoMesConDatos !== filtros.periodo,
  );

  const cambiar = <C extends keyof FiltrosSeriesGlobales>(
    campo: C,
    valor: FiltrosSeriesGlobales[C],
  ) => setFiltros((previos) => ({ ...previos, [campo]: valor }));

  const exportar = async () => {
    setAviso(null);

    try {
      await abrirPdfAutenticado(reportesApi.exportarSeriesGlobales(filtros), {
        nombre: `series-globales-${filtros.tipo.toLowerCase()}-${filtros.periodo}.csv`,
      });
    } catch (fallo) {
      setAviso(fallo instanceof Error ? fallo.message : 'No se pudo exportar el reporte.');
    }
  };

  /** Abre el PDF del reporte (mismo listado, para imprimir o archivar). */
  const exportarPdf = async () => {
    setAviso(null);

    try {
      await abrirPdfAutenticado(reportesApi.pdfSeriesGlobales(filtros));
    } catch (fallo) {
      setAviso(fallo instanceof Error ? fallo.message : 'No se pudo generar el PDF.');
    }
  };

  /** Exporta el mes completo (todos los estados) de un tipo, o consolidado. */
  const exportarConsolidado = async (tipo: string) => {
    setAviso(null);

    try {
      await abrirPdfAutenticado(
        reportesApi.exportarSeriesGlobales({ periodo: filtros.periodo, tipo, estado: '' }),
        { nombre: `series-globales-${tipo.toLowerCase()}-${filtros.periodo}.csv` },
      );
    } catch (fallo) {
      setAviso(fallo instanceof Error ? fallo.message : 'No se pudo exportar el consolidado.');
    }
  };

  const CONSOLIDADOS = [
    { valor: 'COMBUSTIBLE', etiqueta: 'Combustibles' },
    { valor: 'EXTRAS', etiqueta: 'Extras' },
    { valor: 'MARKET', etiqueta: 'Market' },
    { valor: 'GENERAL', etiqueta: 'General' },
  ];

  return (
    <div className="space-y-5">
      <header className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <Link
            to="/reportes"
            className="inline-flex items-center gap-1.5 text-xs font-semibold text-brand-700 hover:underline"
          >
            <ArrowLeft className="size-3.5" aria-hidden="true" />
            Reportes
          </Link>
          <h1 className="mt-1 text-2xl font-bold text-ink-900">Series globales del mes</h1>
          <p className="mt-1 text-sm text-ink-500">
            Lo facturado por serie y producto, con el detalle por estado del comprobante.
          </p>
        </div>

        <div className="flex flex-wrap items-center gap-2">
          <Button
            variant="outline"
            onClick={() => void consulta.refetch()}
            leftIcon={<RefreshCw className="size-4" aria-hidden="true" />}
          >
            Actualizar
          </Button>
          <Button
            variant="outline"
            onClick={exportarPdf}
            leftIcon={<FileText className="size-4" aria-hidden="true" />}
          >
            PDF
          </Button>

          <Button
            variant="outline"
            onClick={exportar}
            leftIcon={<Download className="size-4" aria-hidden="true" />}
          >
            Exportar
          </Button>
        </div>
      </header>

      <Card>
        <CardContent className="space-y-4 p-4">
          <div className="grid items-end gap-3 sm:grid-cols-2 xl:grid-cols-3">
            <label className="flex flex-col gap-1.5 text-sm">
              <span className="font-medium text-ink-700">Mes</span>
              <span className="flex h-11 items-center rounded-control border border-ink-200 bg-white focus-within:border-brand-400 focus-within:ring-4 focus-within:ring-brand-100">
                <CampoFecha
                  tipo="month"
                  value={filtros.periodo}
                  onChange={(evento) => cambiar('periodo', evento.target.value)}
                  className="w-full px-3 text-sm"
                />
              </span>
            </label>

            <label className="flex flex-col gap-1.5 text-sm">
              <span className="font-medium text-ink-700">Tipo de producto</span>
              <Select
                value={filtros.tipo}
                onChange={({ target }) => cambiar('tipo', target.value)}
                className={CONTROL}
                aria-label="Tipo de producto"
              >
                <option value="GENERAL">Todos</option>
                {(catalogos.data?.tipos ?? []).map((tipo) => (
                  <option key={tipo.valor} value={tipo.valor}>
                    {tipo.etiqueta}
                  </option>
                ))}
              </Select>
            </label>

            <label className="flex flex-col gap-1.5 text-sm">
              <span className="font-medium text-ink-700">Estado</span>
              <Select
                value={filtros.estado}
                onChange={({ target }) => cambiar('estado', target.value)}
                className={CONTROL}
                aria-label="Estado del comprobante"
              >
                <option value="">Todos los estados</option>
                {(catalogos.data?.estados ?? []).map((estado) => (
                  <option key={estado} value={estado}>
                    {estado}
                  </option>
                ))}
              </Select>
            </label>
          </div>

          <div className="flex flex-wrap items-center gap-1.5 border-t border-ink-100 pt-3">
            <span className="mr-1 text-xs font-semibold text-ink-500">
              Consolidado del mes (todos los estados)
            </span>
            {CONSOLIDADOS.map((consolidado) => (
              <button
                key={consolidado.valor}
                type="button"
                onClick={() => void exportarConsolidado(consolidado.valor)}
                className="rounded-full border border-ink-200 bg-white px-2.5 py-1 text-xs font-semibold text-ink-600 transition-colors hover:border-brand-200 hover:text-brand-700"
              >
                {consolidado.etiqueta}
              </button>
            ))}
          </div>

          {meses.length > 0 ? (
            <div className="flex flex-wrap items-center gap-1.5 border-t border-ink-100 pt-3">
              <span className="mr-1 inline-flex items-center gap-1.5 text-xs font-semibold text-ink-500">
                <Search className="size-3.5" aria-hidden="true" />
                Meses con comprobantes
              </span>
              {meses.slice(0, 12).map((mes) => (
                <button
                  key={mes.periodo}
                  type="button"
                  aria-pressed={mes.periodo === filtros.periodo}
                  onClick={() => cambiar('periodo', mes.periodo)}
                  className={cn(
                    'rounded-full border px-2.5 py-1 text-xs font-semibold transition-colors',
                    mes.periodo === filtros.periodo
                      ? 'border-brand-300 bg-brand-50 text-brand-700'
                      : 'border-ink-200 bg-white text-ink-600 hover:border-brand-200 hover:text-brand-700',
                  )}
                  title={`${formatNumber(mes.documentos)} comprobante(s)`}
                >
                  {etiquetaMes(mes.periodo)}
                </button>
              ))}
            </div>
          ) : null}
        </CardContent>
      </Card>

      {consulta.isError ? (
        <Alert variant="error" title="No se pudo cargar el reporte">
          {consulta.error instanceof ApiError
            ? consulta.error.message
            : 'Inténtalo de nuevo en unos segundos.'}
        </Alert>
      ) : null}

      {aviso ? (
        <Alert variant="error" title="No se pudo exportar">
          {aviso}
        </Alert>
      ) : null}

      {sinDatos ? (
        <Alert variant="info" title="El mes elegido no tiene comprobantes">
          <p>
            Esta copia tiene comprobantes en {meses.length} mes(es) y el último es{' '}
            {etiquetaMes(ultimoMesConDatos)}.
          </p>
          <Button
            size="sm"
            variant="outline"
            className="mt-2"
            onClick={() => ultimoMesConDatos && cambiar('periodo', ultimoMesConDatos)}
          >
            Ver {etiquetaMes(ultimoMesConDatos)}
          </Button>
        </Alert>
      ) : null}

      <div className="grid grid-cols-2 gap-3 sm:grid-cols-2 xl:grid-cols-4">
        {[
          {
            etiqueta: 'Series',
            valor: formatNumber(series.length),
            detalle: filtros.estado === '' ? 'con movimiento en el mes' : `en ${filtros.estado}`,
          },
          {
            etiqueta: 'Productos',
            valor: formatNumber(new Set(filas.map((fila) => fila.producto_id)).size),
            detalle: `${formatNumber(filas.length)} combinación(es)`,
          },
          {
            etiqueta: 'Cantidad',
            valor: CANTIDAD.format(totales.cantidad),
            detalle: 'unidades y litros facturados',
          },
          {
            etiqueta: 'Monto',
            valor: formatCurrency(totales.total),
            detalle: etiquetaMes(consulta.data?.periodo),
          },
        ].map((tarjeta) => (
          <Card key={tarjeta.etiqueta}>
            <CardContent className="p-4">
              <p className="text-xs font-semibold tracking-wide text-ink-500 uppercase">
                {tarjeta.etiqueta}
              </p>
              <p className="text-lg font-bold text-ink-900">{tarjeta.valor}</p>
              <p className="text-xs text-ink-500">{tarjeta.detalle}</p>
            </CardContent>
          </Card>
        ))}
      </div>

      <Card>
        <CardContent className="space-y-3 p-4">
          <div className="flex flex-wrap items-center justify-between gap-2">
            <h2 className="text-sm font-semibold text-ink-800">Resumen por estado</h2>
            <span className="text-xs text-ink-500">
              Toca una fila para ver sólo ese estado · {etiquetaMes(consulta.data?.periodo)}
            </span>
          </div>

          {consulta.isLoading ? <Skeleton className="h-40 w-full" /> : null}

          {!consulta.isLoading ? (
            <div className="overflow-y-auto rounded-card border border-ink-100">
              <table className="w-full border-collapse text-[13px]">
                <thead className="bg-white shadow-[0_1px_0_0_var(--color-ink-100)]">
                  <tr className="text-left text-[11px] font-semibold tracking-wide text-ink-500 uppercase">
                    <th className="px-2 py-2">Estado</th>
                    <th className="px-2 py-2 text-right">Series</th>
                    <th className="px-2 py-2 text-right">Combinaciones</th>
                    <th className="px-2 py-2 text-right">Cantidad</th>
                    <th className="px-2 py-2 text-right">Monto</th>
                  </tr>
                </thead>
                <tbody>
                  {porEstado.length === 0 ? (
                    <tr className="border-t border-ink-100">
                      <td colSpan={5} className="px-2 py-6 text-center text-sm text-ink-500">
                        No hay comprobantes de este tipo en el mes.
                      </td>
                    </tr>
                  ) : null}

                  {porEstado.map((fila) => (
                    <tr
                      key={fila.estado}
                      onClick={() =>
                        cambiar('estado', filtros.estado === fila.estado ? '' : fila.estado)
                      }
                      className={cn(
                        'cursor-pointer border-t border-ink-100 hover:bg-brand-50/40',
                        filtros.estado === fila.estado && 'bg-brand-50/60',
                      )}
                    >
                      <td className="px-2 py-2">
                        <span
                          className={cn(
                            'font-medium',
                            filtros.estado === fila.estado ? 'text-brand-700' : 'text-ink-800',
                          )}
                        >
                          {fila.estado}
                        </span>
                      </td>
                      <td className="px-2 py-2 text-right text-ink-600">
                        {formatNumber(fila.series.size)}
                      </td>
                      <td className="px-2 py-2 text-right text-ink-600">
                        {formatNumber(fila.filas)}
                      </td>
                      <td className="px-2 py-2 text-right text-ink-600">
                        {CANTIDAD.format(fila.cantidad)}
                      </td>
                      <td className="px-2 py-2 text-right font-semibold text-ink-900">
                        {formatCurrency(fila.total)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : null}
        </CardContent>
      </Card>

      <Card>
        <CardContent className="space-y-3 p-4">
          <div className="flex flex-wrap items-center justify-between gap-2">
            <h2 className="text-sm font-semibold text-ink-800">
              Detalle por serie y producto ({formatNumber(filas.length)})
            </h2>
            <span className="text-xs text-ink-500">
              {catalogos.data?.tipos.find((tipo) => tipo.valor === filtros.tipo)?.etiqueta} ·{' '}
              {filtros.estado === '' ? 'todos los estados' : filtros.estado}
            </span>
          </div>

          {consulta.isLoading ? <Skeleton className="h-64 w-full" /> : null}

          {!consulta.isLoading ? (
            <div className="max-h-[60vh] overflow-y-auto rounded-card border border-ink-100">
              <table className="w-full border-collapse text-[13px]">
                <thead className="sticky top-0 z-10 bg-white shadow-[0_1px_0_0_var(--color-ink-100)]">
                  <tr className="text-left text-[11px] font-semibold tracking-wide text-ink-500 uppercase">
                    <th className="px-2 py-2">Serie / producto</th>
                    <th className="px-2 py-2 text-right">Cantidad</th>
                    <th className="px-2 py-2 text-right">Precio estimado</th>
                    <th className="px-2 py-2 text-right">Monto</th>
                  </tr>
                </thead>
                <tbody>
                  {series.length === 0 ? (
                    <tr className="border-t border-ink-100">
                      <td colSpan={4} className="px-2 py-10 text-center text-sm text-ink-500">
                        No hay facturación de este tipo con los filtros elegidos.
                      </td>
                    </tr>
                  ) : null}

                  {paginacion.visibles.map((grupo) => (
                    <Fragment key={grupo.serie}>
                      <tr className="border-t border-ink-100 bg-ink-50/60">
                        <td className="px-2 py-2 font-semibold text-ink-800">
                          Serie {grupo.serie}
                          <span className="ml-1 font-normal text-ink-500">
                            · {formatNumber(grupo.filas.length)} producto(s)
                          </span>
                        </td>
                        <td className="px-2 py-2 text-right font-semibold text-ink-700">
                          {CANTIDAD.format(grupo.cantidad)}
                        </td>
                        <td className="px-2 py-2" />
                        <td className="px-2 py-2 text-right font-semibold text-ink-900">
                          {formatCurrency(grupo.total)}
                        </td>
                      </tr>

                      {grupo.filas
                        .slice()
                        .sort((a, b) => Number(b.total ?? 0) - Number(a.total ?? 0))
                        .map((fila) => (
                          <tr
                            key={`${grupo.serie}-${fila.estado}-${fila.producto_id}`}
                            className="border-t border-ink-100 hover:bg-brand-50/30"
                          >
                            <td className="px-2 py-2 pl-6 text-ink-800">
                              {fila.producto}
                              {filtros.estado === '' ? (
                                <span className="ml-1.5 text-[11px] text-ink-400">{fila.estado}</span>
                              ) : null}
                            </td>
                            <td className="px-2 py-2 text-right text-ink-600">
                              {fila.cantidad ? CANTIDAD.format(Number(fila.cantidad)) : '—'}
                            </td>
                            <td className="px-2 py-2 text-right text-ink-600">
                              {fila.cantidad && Number(fila.cantidad) > 0 && fila.total
                                ? formatNumber(Number(fila.total) / Number(fila.cantidad))
                                : '—'}
                            </td>
                            <td className="px-2 py-2 text-right font-semibold text-ink-900">
                              {fila.total ? formatCurrency(Number(fila.total)) : '—'}
                            </td>
                          </tr>
                        ))}
                    </Fragment>
                  ))}
                </tbody>
                {series.length > 0 ? (
                  <tfoot className="sticky bottom-0 bg-white shadow-[0_-1px_0_0_var(--color-ink-100)]">
                    <tr className="text-[13px] font-semibold text-ink-800">
                      <td className="px-2 py-2">
                        TOTAL · {formatNumber(series.length)} serie(s)
                      </td>
                      <td className="px-2 py-2 text-right">{CANTIDAD.format(totales.cantidad)}</td>
                      <td className="px-2 py-2" />
                      <td className="px-2 py-2 text-right">{formatCurrency(totales.total)}</td>
                    </tr>
                  </tfoot>
                ) : null}
              </table>
            </div>
          ) : null}

          {!consulta.isLoading ? <Paginador {...paginacion} etiqueta="series" /> : null}
        </CardContent>
      </Card>

      <p className="flex items-start gap-2 text-xs text-ink-500">
        <Info className="mt-0.5 size-3.5 shrink-0" aria-hidden="true" />
        Se suman las facturas, las boletas, las ventas de administración y las ventas al crédito del
        mes (y los documentos de Market, por la descripción de cada ítem). El precio estimado es el
        monto entre la cantidad, y la exportación trae el detalle con los mismos números.
      </p>
    </div>
  );
}
